import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { daDK } from '@mui/x-data-grid/locales';
import { useMemo, useState } from 'react';
import { getTypeColor, getTypeIcon } from '../../../../utils/nodeTypeMapper';
import SeuDatagridToolbar from './SeuDatagridToolbar';
import { formatNumber } from '@/utils/formatters';
import { useTranslation } from 'react-i18next';

const CustomHeaderWithLabel = ({ label, props }) => {
  return (
    <Tooltip
      title={
        <>
          <Typography variant="body2">{props.colDef.headerName}</Typography>
          <Typography variant="body5">{props.colDef.description}</Typography>
        </>
      }
      placement="top"
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="end"
        justifyContent="space-between"
        width="100%"
        height="100%"
      >
        <div
          style={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
            height: '100%',
          }}
        >
          <Typography
            variant="body2"
            sx={{ overflow: 'visible', whiteSpace: 'normal' }}
            fontWeight={'bold'}
          >
            {props.colDef.headerName}
          </Typography>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Typography
            variant="body5"
            fontSize={{ xs: 10, sm: 11, md: 12 }}
            color={'gray'}
          >
            {label}
          </Typography>
        </div>
      </Box>
    </Tooltip>
  );
};

function weightedAverage(data) {
  const [weightedSum, totalPower] = data.reduce(
    ([accW, accP], [co2ePerKWh, power]) => [
      accW + co2ePerKWh * power, // Σ(intensity · weight)
      accP + power, // Σ(weight)
    ],
    [0, 0],
  );

  return totalPower ? weightedSum / totalPower : 0;
}

const quarterKey = (year, month) => `Q${Math.ceil(month / 3)} ${year}`; // → “Q3 2024”
const yearKey = (year) => String(year); // “2024” // <<< NEW

const SeuDatagrid = ({ data = [] }) => {
  const { t } = useTranslation('energyxplorer-esg');
  const [showCO2, setShowCO2] = useState(true);
  const [showYears, setShowYears] = useState(false);

  const quarterDescription = (headerName) => {
    const q = headerName.split(' ')[0];
    return (
      {
        Q1: t('seu_distribution_datagrid.columns.Q1'),
        Q2: t('seu_distribution_datagrid.columns.Q2'),
        Q3: t('seu_distribution_datagrid.columns.Q3'),
        Q4: t('seu_distribution_datagrid.columns.Q4'),
      }[q] ?? t('seu_distribution_datagrid.columns.Q_any')
    );
  };

  const metrics = useMemo(
    () => [
      {
        key: 'PowerEligible',
        suffix: 'power',
        label: t('consumption'),
        unit: t('consumption_unit'),
        decimals: 0,
      },
      {
        key: 'CO2eEnergy',
        suffix: 'co2e',
        label: t('emission'),
        unit: t('emission_unit'),
        decimals: 0,
      },
      {
        key: 'CO2ePerkWh',
        suffix: 'co2e_per_kwh',
        label: t('decleration'),
        unit: t('decleration_unit'),
        decimals: 1,
      },
    ],
    [t],
  );
  /* ---------- 1a. discover all years & quarters ---------- */

  const years = useMemo(
    // <<< NEW
    () =>
      Array.from(new Set(data.flatMap((r) => r.Data.map((d) => d.year)))).sort(
        (a, b) => a - b,
      ),
    [data],
  );

  const quarters = useMemo(
    () =>
      Array.from(
        new Set(
          data.flatMap((r) => r.Data.map((d) => quarterKey(d.year, d.month))),
        ),
      ).sort((a, b) => {
        const [qa, ya] = a.split(' ');
        const [qb, yb] = b.split(' ');
        return ya !== yb ? +ya - +yb : +qa[1] - +qb[1];
      }),
    [data],
  );

  /* ---------- 2. build columns & column groups ---------- */
  const columns = useMemo(
    () => [
      {
        field: 'icon',
        headerName: t('seu_distribution_datagrid.columns.seu'),
        width: 60,
        renderCell: (params) => (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Avatar
              variant="rounded"
              size="small"
              sx={{
                backgroundColor: getTypeColor(params.value) || '#555',
                color: '#fff',
                borderRadius: 2,
              }}
            >
              <i className={`fas ${getTypeIcon(params.value)}`}></i>
            </Avatar>
          </Box>
        ),
      },
      {
        field: 'name',
        headerName: t('seu_distribution_datagrid.columns.name'),
        width: 160,
        pinned: 'left',
      },

      /* ----- (A) grand-total columns you already had ----- */
      ...metrics.map((m) => ({
        field: `total_${m.suffix}`,
        headerName: m.label,
        description: t('seu_distribution_datagrid.columns.total_sum_all_years'),
        type: 'number',
        width: 130,
        renderHeader: (p) => <CustomHeaderWithLabel props={p} label={m.unit} />,
        valueFormatter: (v) => formatNumber(v),
      })),

      /* ----- (B) NEW: per-year totals ----- */
      ...years.flatMap((y) =>
        metrics.map((m) => ({
          field: `${y}_${m.suffix}`, // 2024_power
          headerName: m.label,
          description: t('seu_distribution_datagrid.columns.total_sum_year', {
            yaer: y,
          }),
          type: 'number',
          width: 130,
          renderHeader: (p) => (
            <CustomHeaderWithLabel props={p} label={m.unit} />
          ),
          valueFormatter: (v) => formatNumber(v),
        })),
      ),

      /* ----- (C) existing per-quarter columns ----- */
      ...quarters.flatMap((q) =>
        metrics.map((m) => ({
          field: `${q.replace(' ', '_')}_${m.suffix}`,
          headerName: m.label,
          description: quarterDescription(q),
          type: 'number',
          width: 130,
          renderHeader: (p) => (
            <CustomHeaderWithLabel props={p} label={m.unit} />
          ),
          valueFormatter: (v) => formatNumber(v),
        })),
      ),
    ],
    [t, metrics, years, quarters],
  );

  const columnGroupingModel = useMemo(
    () => [
      {
        groupId: 'Total',
        headerName: t('total'),
        description: t('total_sum'),
        children: metrics.map((m) => ({ field: `total_${m.suffix}` })),
      },
      /* ----- (B) NEW: per-year group headers ----- */
      ...years.map((y) => ({
        groupId: y.toString(),
        headerName: y.toString(),
        description: t('seu_distribution_datagrid.columns.total_sum_year', {
          yaer: y,
        }),
        children: metrics.map((m) => ({ field: `${y}_${m.suffix}` })),
      })),
      /* ----- (C) existing per-quarter groups ----- */
      ...quarters.map((q) => ({
        groupId: q,
        headerName: q,
        description: quarterDescription(q),
        children: metrics.map((m) => ({
          field: `${q.replace(' ', '_')}_${m.suffix}`,
        })),
      })),
    ],
    [t, metrics, years, quarters],
  );

  /* ---------- 3. build rows ---------- */
  const rows = useMemo(
    () =>
      data.map((row, idx) => {
        // ① running sums for additive metrics
        const agg = { total_power: 0, total_co2e: 0 };

        for (const d of row.Data) {
          const qBase = quarterKey(d.year, d.month).replace(' ', '_');
          const yBase = yearKey(d.year);

          metrics.forEach((m) => {
            // CO2ePerkWh is **derived**, not additive → skip it here

            const qField = `${qBase}_${m.suffix}`;
            const yField = `${yBase}_${m.suffix}`;
            const qFieldCoefficient = `${qBase}_coefficient`;
            const yFieldCoefficient = `${yBase}_coefficient`;
            const val = d[m.key];

            if (m.key === 'CO2ePerkWh') {
              agg[qFieldCoefficient] = [
                ...(agg[qFieldCoefficient] ?? []),
                [d.CO2ePerkWh, d.PowerEligible],
              ];
              agg[yFieldCoefficient] = [
                ...(agg[yFieldCoefficient] ?? []),
                [d.CO2ePerkWh, d.PowerEligible],
              ];
              agg[`total_coefficient`] = [
                ...(agg['total_coefficient'] ?? []),
                [d.CO2ePerkWh, d.PowerEligible],
              ];
              return;
            }

            /* per-quarter */
            agg[qField] = (agg[qField] ?? 0) + val;
            /* per-year */
            agg[yField] = (agg[yField] ?? 0) + val;
            /* grand total */
            agg[`total_${m.suffix}`] += val;
          });
        }

        // grand-total intensity
        agg.total_co2e_per_kwh = weightedAverage(agg.total_coefficient);

        agg.total_co2e = agg.total_co2e / 1000; // convert to kg CO2e
        // per-year intensity
        years.forEach((y) => {
          agg[`${y}_co2e_per_kwh`] = weightedAverage(agg[`${y}_coefficient`]);
          agg[`${y}_co2e`] = agg[`${y}_co2e`] / 1000; // convert to kg CO2e
        });

        // per-quarter intensity
        quarters.forEach((q) => {
          const key = q.replace(' ', '_');

          agg[`${key}_co2e_per_kwh`] = weightedAverage(
            agg[`${key}_coefficient`],
          );
          agg[`${key}_co2e`] = agg[`${key}_co2e`] / 1000; // convert to kg CO2e
        });

        // ③ done
        return { id: idx, icon: row.Name, name: row.Name, ...agg };
      }),
    [data, years, quarters, metrics], // ← added deps
  );

  const columnVisibilityModel = useMemo(() => {
    const visibleKeys = showCO2
      ? ['CO2eEnergy', 'CO2ePerkWh']
      : ['PowerEligible'];

    const visibleTotalKeys = showCO2 ? ['CO2eEnergy'] : ['PowerEligible'];

    const model = {};

    const isVisible = (metricKey) => visibleKeys.includes(metricKey);
    const isTotalsVisible = (metricKey) => visibleTotalKeys.includes(metricKey);

    // grand-totals
    metrics.forEach((m) => {
      model[`total_${m.suffix}`] = isTotalsVisible(m.key);
    });

    // yearly & quarterly
    years.forEach((y) =>
      metrics.forEach((m) => {
        model[`${y}_${m.suffix}`] = showYears ? isTotalsVisible(m.key) : false;
      }),
    );
    quarters.forEach((q) =>
      metrics.forEach((m) => {
        model[`${q.replace(' ', '_')}_${m.suffix}`] = showYears
          ? false
          : isVisible(m.key);
      }),
    );

    return model;
  }, [showCO2, metrics, years, quarters, showYears]);

  /* ---------- 4. render ---------- */
  return (
    <Box sx={{ width: '100%' }}>
      <DataGridPremium
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        columnVisibilityModel={columnVisibilityModel} // ← new
        hideFooter
        pinnedRows={{
          top:
            rows.length > 0 ? [rows.find((o) => o.name === 'Hovedmåler')] : [],
        }}
        localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
        pin
        slots={{
          toolbar: SeuDatagridToolbar,
        }}
        slotProps={{
          toolbar: {
            setShowCO2: setShowCO2,
            setShowYears: setShowYears,
            showCO2: showCO2,
            showYears: showYears,
          },
        }}
        sx={{
          borderRadius: 2,
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, \
             & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
          '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
          '& .MuiDataGrid-main': (theme) => ({
            backgroundColor:
              theme.palette.mode === 'light' ? '#fff' : '#121212',
          }),
          '& .MuiDataGrid-toolbarContainer': (theme) => ({
            backgroundColor:
              theme.palette.mode === 'light' ? '#fff' : '#121212',
          }),
          '& .MuiDataGrid-root': (theme) => ({
            borderColor: theme.palette.divider,
          }),
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
          '& .selected-row': {
            backgroundColor: (theme) => theme.palette.action.selected,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.action.selected,
            },
          },
        }}
      />
    </Box>
  );
};

export default SeuDatagrid;
